import {
  AfterContentChecked,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit
} from '@angular/core';
import { DomSanitizer, Title } from '@angular/platform-browser';
import { CsIconRegistryService } from '@cbuild/controls';
import {
  ApplicationService,
  AuthenticationService,
  FirebaseMessagingService
} from '@cbuild/core';
import { FADEIN_FADEOUT, themeList, Themes } from '@cbuild/shared';
import { Subject, takeUntil } from 'rxjs';

import { WebSocketService } from './core/services/ws/web-socket.service';

const ANONYMOUS_ROUTES = [
  '/accounts/reset-password',
  '/accounts/create-user',
  '/accounts/no-access'
];

function isIgnoredRoute(url: string): boolean {
  for (const route of ANONYMOUS_ROUTES) {
    if (route === url) {
      return true;
    }
  }

  return false;
}

@Component({
  selector: 'cs-root',
  standalone: false,
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  animations: [FADEIN_FADEOUT]
})
export class AppComponent implements OnInit, AfterContentChecked, OnDestroy {
  public onDestroy = new Subject();
  public initialized: boolean;
  public authInitialized = false;

  constructor(
    private authenticationService: AuthenticationService,
    private iconRegistry: CsIconRegistryService,
    private sanitizer: DomSanitizer,
    private applicationService: ApplicationService,
    private messagingService: FirebaseMessagingService,
    private titleService: Title,
    private wsService: WebSocketService,
    private cdr: ChangeDetectorRef
  ) {
    this.titleService.setTitle('C.Build');
  }

  public async ngOnInit(): Promise<void> {
    this.registerIcons();
    await this.authenticationService.initialize();
    this.authInitialized = true;
    this.authenticationService.user$
      .pipe(takeUntil(this.onDestroy))
      .subscribe(_ => {
        if (this.authenticationService.isAuthenticated) {
          this.messagingService.initialize();
          this.wsService.connect();
        } else {
          this.wsService.disconnect();
        }
        const themeLookUpString = `${this.authenticationService.orgId}:`;
        const reqTheme = _?.theme
          .split(',')
          .find(x => x.startsWith(themeLookUpString));
        const reqThemeId = reqTheme?.replace(themeLookUpString, '');
        const themeId = reqThemeId
          ? (`${reqThemeId}` as Themes) || Themes.THEME_1
          : Themes.THEME_1;
        const theme = themeList[themeId];
        this.applicationService.updateTheme(theme.primary, theme.secondary);
      });
  }

  public ngAfterContentChecked(): void {
    this.cdr.detectChanges();
  }

  public ngOnDestroy(): void {
    this.onDestroy.next(0);
    this.onDestroy.complete();
  }

  public updateInitialization(): void {
    this.initialized = true;
  }

  public get canRouteBeIgnored(): boolean {
    return isIgnoredRoute(location.pathname);
  }

  public get isAuthenticated(): boolean {
    return this.authInitialized && this.authenticationService.isAuthenticated;
  }

  private registerIcons(): void {
    [
      'dashboard',
      'reports',
      'settings',
      'notification',
      'down-arrow',
      'tick',
      'onboarding',
      'right-arrow',
      'pencil',
      'close',
      'edit',
      'trash',
      'plus',
      'pencil-edit',
      'plots',
      'commercial',
      'residential',
      'download',
      'upload',
      'edit-orange',
      'call-activity',
      'email-activity',
      'note-activity',
      'site-visit-activity',
      'flat-interest',
      'task-activity',
      'info-activity',
      'total-flats',
      'sold',
      'site',
      'conversions',
      'available',
      'total-leads',
      'not-authorized',
      'no-activities-found',
      'filter',
      'sales',
      'double-left',
      'double-right',
      'account-info',
      'basic-info',
      'companies',
      'form-properties',
      'leads',
      'nav-notification',
      'onboarding-left-nav',
      'project',
      'projects',
      'security',
      'user-access',
      'create-lead',
      'search',
      'information',
      'download-arrow',
      'upload-arrow',
      'nodata-found',
      'notification-close',
      'lead-snapshot',
      'team-performance',
      'star',
      'daily-activity',
      'no-notifications',
      'drag-drop',
      'copy',
      'completed',
      'pending',
      'cancelled',
      'deferred',
      'monitoring',
      'campaigns',
      'crm',
      'ops',
      'drawing-stage',
      'drawing-type',
      'no-projects',
      'amenities',
      'floor-plan',
      'gallery',
      'overview',
      'pencil-edit-button',
      'pricing',
      'specifications',
      'model-apartment',
      'invalid-lnk',
      'link-expired',
      'stripped-tie',
      'user-name',
      'mobile-info',
      'email-info',
      'rupee',
      'account-settings',
      'profile-preferences',
      'customers',
      'feedback',
      'integration',
      'lead-bucket',
      'lead-priority',
      'pipeline',
      'activity',
      'performance',
      'snapshot',
      'call',
      'cancel',
      'completed-icon',
      'differed-icon',
      'email',
      'overdue',
      'site-visit',
      'site-visit-pending',
      'todo',
      'upcoming',
      'due-date',
      'open-task',
      'create-icon',
      'traditional-campaign',
      'cinema',
      'hoarding',
      'magazine',
      'news',
      'pamphlet',
      'radio-icon',
      'television',
      'pdf',
      'products-services',
      'theme-select',
      'inProgress',
      'ps-showcase',
      'tick-inside-circle',
      'ps-sales-marketing',
      'ps-crm',
      'communications',
      'card-close',
      'show-case-pro',
      'sub-source',
      'marketing',
      'ops-lpb',
      'campaigns-lpb',
      'monitoring-lpb',
      'crm-lpb',
      'settings-lpb',
      'account-settings-lpb',
      'profile-preferences-lpb',
      'reports-lpb',
      'showcase',
      'created-on',
      'created-by',
      'lead-stage',
      'email-plane',
      'mobile-activity',
      'tick-stage',
      'project-name',
      'user-leadcard',
      'assigned-leadcard',
      'email-leadcard',
      'mobile-leadcard',
      'card-arrow',
      'ads-account',
      'block-icon',
      'facing-icon',
      'flatno-icon',
      'floor-icon',
      'size-icon',
      'unit-type-icon',
      'ageoflead-lft',
      'conversation',
      'lead-sitevisit',
      'integration-icon',
      'status-call',
      'kyc-approved',
      'docs-uploaded',
      'total-customers',
      'kyc-pending',
      'discount-request',
      'crm-left-icon',
      'work-flow',
      'discount-process',
      'call-customer',
      'conference',
      'conference-call',
      'filter-lead',
      'calendar-filter',
      'download-long-arrow',
      'call-outbound',
      'call-queue',
      'stage',
      'book',
      'call-inbound',
      'missed-call',
      'call-conversation',
      'booked-user',
      'cancel-unit',
      'document-notification',
      'payment-notification',
      'discount-percent',
      'to-do-task',
      'discount',
      'more',
      'clock',
      'resend-psw',
      'building',
      'email-send',
      'timeline',
      'view-document',
      'project-document',
      'view-details',
      'flat-transfer',
      'unit-change-request',
      'document-upload',
      'pay-now',
      'cancel-request',
      'resend-password',
      'general-settings',
      'booking',
      'terms-and-conditions',
      'refund',
      'whats-app',
      'default-organization',
      'default-organization-white',
      'approved',
      'rejected',
      'lead-request',
      'facebook',
      'split',
      'eye',
      'demand-letter',
      'tag',
      'pdf-icon',
      'up-arrow',
      'user',
      'stage-snapshot',
      'total-event',
      'unique-events',
      'event-value',
      'sales-inventory',
      'down-angle',
      'unit-customization',
      'attachments',
      'history-line',
      'share',
      'folder',
      'yellow-folder',
      'plus-round',
      'docs',
      'document',
      'visualization',
      'schedule',
      'construction-material',
      'wrench',
      'whatsapp',
      'transparent-circle',
      'drag-drop-large',
      'site-document-approval',
      'microsoft-word',
      'dwg',
      'ifc',
      'revit',
      'microsoft-excel',
      'left-arrow',
      'whatsapp',
      'left-arrow',
      'total-properties',
      'total-units',
      'total-available',
      'total-sold',
      'total-reserved',
      'total-mortgaged',
      'total-not-for-sale',
      'total-land-owner',
      'minus-line-icon',
      'full-screen',
      'minimize'
    ].forEach(val => {
      this.iconRegistry.addSvgIcon(
        val,
        this.sanitizer.bypassSecurityTrustResourceUrl(`/assets/svg/${val}.svg`)
      );
    });
  }
}
